<template>
  <!-- prettier-ignore -->
  <div class="screen">
    <Form.Form
      class="form"
      name="changeAuthInfo"
      tracking-disabled
      :validator="v"
      @cancel="cancel"
      @submit="submit"
    >
      <template v-if="!isVerificationNeeded">
        <Modal.Content>
          <div class="frames__frame">
            <the-typography
              type="heading04"
              v-text="$t('screen.onboarding.title')"
            />
            <the-typography
              type="bodyMediumLong"
              v-text="$t('screen.onboarding.existingCustomerText')"
            />
            <Form.PhoneInput
              v-model="formData.mobile"
              class="form__input"
              autofocus
              name="mobile"
              required
              theme="light"
              :delay="10000"
              :placeholder="$t('person.mobile')"
              :title-label="$t('person.mobile')"
              :validate="v.mobile"
            />
            <Form.Input
              v-model="formData.email"
              class="form__input"
              name="email"
              required
              theme="light"
              type="email"
              :placeholder="$t('person.email')"
              :title-label="$t('person.email')"
              :validate="v.email"
            />
          </div>
        </Modal.Content>
      </template>
      <template v-else>
        <Modal.Content>
          <div class="frames__frame">
            <the-typography
              type="heading04"
              v-text="$t('screen.onboarding.title')"
            />
            <the-typography
              v-if="!isTestEnv"
              type="bodyMediumLong"
              v-text="$t('screen.reentry.tanHelptext', {
                mobile: formData.mobile
              })"
            />
            <Form.CodeInput
              v-model="formData.code"
              name="code"
              numeric
              required
              theme="light"
              :title-label="$t('screen.reentry.insertTan')"
            />
          </div>
        </Modal.Content>
      </template>

      <Modal.Footer class="frames__footer">
        <template #secondary>
          <div class="frames__actions">
            <span>
              <Form.Button
                v-if="isVerificationNeeded"
                link
                :disabled="isSmsDisabled"
                :text="$t('screen.reentry.resend')"
                @click="submit(false)"
              />
            </span>
            <Form.Button
              link
              :disabled="isVerificationNeeded"
              :text="$t('form.cancel')"
              @click="cancel"
            />
          </div>
        </template>
        <template #primary>
          <Form.Button
            primary
            type="submit"
            :text="$t('form.saveAndContinue')"
          />
        </template>
      </Modal.Footer>
    </Form.Form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'

import useVuelidate from '@vuelidate/core'

import { events$, loading$ } from '@/services'
import { validations } from '@/validations/onboarding'

import basketStore from '@/store/basket'
import sessionStore from '@/store/session'
import useBrowser from '@/hooks/useBrowser'

import * as Form from '@/components/Form'
import * as Modal from '@/components/Modal'

import { EVENT_BASKET, SOURCE } from '@/config/events'

// HOOKS
const { browser } = useBrowser()

// INIT
const emit = defineEmits(['close'])
const isTestEnv = import.meta.env.VITE_ENV_KEY !== 'PRD'

// DATA
const formData = reactive({
  code: undefined,
  email: basketStore.basket.email,
  mobile: basketStore.basket.mobile,
})
const isSmsDisabled = ref(false)
const isVerificationNeeded = ref(false)

// METHODS
function cancel() {
  emit('close')
}

async function submit(allowToken) {
  loading$.start()

  isSmsDisabled.value = true
  window.setTimeout(() => (isSmsDisabled.value = false), 60 * 2 * 1000)

  const params = {
    isDesktop: browser.isDesktop,
    emailType: 'OFFER_REENTRY',
  }

  if (allowToken && isVerificationNeeded.value) {
    params.mobileNumberVerificationSmsTan = formData.code
    delete formData.code
  }
  let __basket = formData

  try {
    __basket = await basketStore.updateOvpBasket(__basket, {}, params)
    events$.emit(EVENT_BASKET.SAVED, {
      basket: __basket,
      source: SOURCE.ONBOARDING,
      update: true,
    })

    cancel()
  } catch (error) {
    if (error?.messages?.find(m => m.errorCode === 'MOBILE_NUMBER_VERIFICATION_REQUIRED')) {
      sessionStore.setSession(__basket)
      basketStore.persistBasket(__basket)
      isVerificationNeeded.value = true
    }
  }

  loading$.end()
}

// VALIDATION
const v = useVuelidate(validations, formData, { $lazy: true })
</script>

<style name="mobile" scoped>
.screen {
  .frames {
    display: flex;
    flex-direction: column;
    gap: 26px;

    .form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .frames__frame {
    background-color: var(--c-primary-neutral-2);
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .frames__footer :deep(.actions__secondary) {
    flex: 1;
  }

  .frames__actions {
    display: flex;
    flex: 1;
    justify-content: space-between;
    gap: 16px;

    div:last-child {
      display: flex;
      gap: 16px;
      align-self: flex-end;
    }
  }
}

.policy__title {
  font-weight: bold;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .screen {
    .frames__actions {
      display: flex;
      justify-content: space-between;
      gap: 16px;

      div:last-child {
        display: flex;
        gap: 16px;
        align-self: flex-end;
      }
    }
  }
}
</style>
